import type { LiveResponse, LiveVehicle, Motion, TrackerHealth, VehicleState, VehicleStatus } from './types';
import { normalizeTrackerHealth } from './trackerHealth';
import { normalizeVehicleState } from './vehicleState';

/**
 * The `/ws` frame envelope — seam S3, consumed VERBATIM from raad-api
 * (`api/src/modules/live/frames.ts`). Not imported across trees: it is the wire
 * contract, mirrored here and pinned by tests, never extended by the client.
 *
 *   { type, vehicleId, payload, ts }
 *
 * `vehicleId` carries OUR vehicle uuid — the value of the `id` field on the item
 * the client already rendered from `/api/vehicles/live`. It is NEVER the Traccar
 * device id (that never leaves the server). A frame is therefore applied onto the
 * existing item by `vehicleId`: the volatile live fields are replaced, the stable
 * identity fields (id/plate/label/model/plan) are kept from the initial fetch —
 * exactly the fields `payload` deliberately omits.
 *
 * `type` reflects which Traccar array the update came from ('position' vs
 * 'status'); the payload is a coherent full live model either way, so the client
 * applies it identically. It is informational only.
 */

/**
 * The volatile per-frame fields — the SAME projection `/api/vehicles/live`
 * renders, minus identity. Mirrors `LiveFrameFields` in the API's view.ts.
 *
 * `speedKmh` and `course` are nullable on the wire (a cache miss carries no
 * speed); the client's `LiveVehicle` models them as non-null, so `applyFrame`
 * coalesces a null to 0 — a vehicle the server derived as `moving` always
 * carries a real speed, so this only ever fills a not-moving gap.
 */
export interface LiveFrameFields {
  status: VehicleStatus;
  positionFixedAt: string | null;
  positionAgeSec: number | null;
  trackerContactAt: string | null;
  freshnessDiverged: boolean;
  lat: number | null;
  lon: number | null;
  speedKmh: number | null;
  /** 15e seam — server-derived: was the last stored fix at/above the moving
   *  threshold? Chooses `engineStale`'s sub-line. OPTIONAL on the wire, like
   *  `lastMovingCourse`: an older API build omits it, and absent must mean "keep
   *  what we had", never a default that states an observation we never made. */
  lastFixMoving?: boolean | null;
  /** SLICE B — open overspeed episode? OPTIONAL on the wire so an older API build
   *  does not fail the frame. 🔴 Unlike `lastFixMoving`/`lastMovingCourse`, ABSENT
   *  COALESCES TO `false` RATHER THAN RETAINING — a deliberate departure from this
   *  file's retain idiom, for asymmetric cost: retaining a stale `true` leaves a
   *  marker red after the vehicle has slowed, a false alarm that contradicts the
   *  feed and that the customer cannot clear; a briefly-lost red self-corrects.
   *
   *  🔴 WHY IT SELF-CORRECTS — and it did NOT before the API-side Slice-B fix, which
   *  is why this note is now stated as what actually runs rather than as an
   *  aspiration. ALL THREE API projection paths emit the CURRENT flag: the REST
   *  /live item, the Redis-backed frame, and the outgoing WS projection
   *  (`projectAndPublish`), whose in-process buffer re-reads `overspeedOpen` from
   *  Redis before every publish (api/src/modules/live/traccarSocket.ts,
   *  `refreshBufferForPublish`). So a present frame always carries the true value and
   *  the next frame restores a red that an absent-field frame dropped. Absent
   *  therefore means only an OLDER API build (or a Redis-read failure, which degrades
   *  the whole read path anyway) — NOT a warm device the WS path forgot. That last
   *  case WAS the bug: the WS frame never carried `true` for an already-buffered
   *  device, so this coalesce stranded the map green for the whole episode, not for a
   *  single frame. Both ingest paths (fetch and frame) coalesce the same way, so they
   *  cannot disagree during a rolling deploy. */
  speeding?: boolean;
  course: number | null;
  /**
   * Seam S3-W — last heading seen while moving, retained while stopped (degrees
   * 0–360, or null when never observed moving). OPTIONAL and possibly null on the
   * wire. Two of the API's three projection paths read Redis and emit it always
   * (`deriveLiveFields` → the REST /live item and the Redis-backed frame). The
   * THIRD path — the outgoing WS projection `projectAndPublish`
   * (api/src/modules/live/traccarSocket.ts) — builds the frame from an in-process
   * buffer that is COLD after any Traccar-socket reconnect (a deploy, a network
   * blip). A parked vehicle's next stopped frame then merges into that cold buffer
   * and carries no heading → null (or absent). So the client must never treat a
   * null/absent here as "no heading"; `applyFrame` RETAINS the prior baseline.
   */
  lastMovingCourse?: number | null;
  /**
   * Seam S1 — tracker health. OPTIONAL on the wire: a frame that does not carry it
   * (the field is `undefined`) leaves the last-known health untouched, exactly like a
   * frame from a cold projection buffer. A frame that DOES carry it — an object, or an
   * explicit `null` for "no telemetry now" — is authoritative and replaces it. The
   * two are distinguished by `undefined` vs present in `applyFrame`, so a genuine
   * transition to no-telemetry is not masked by retain-through. Value is normalised on
   * apply, never trusted verbatim off the wire.
   */
  trackerHealth?: TrackerHealth | null;
  cacheAgeSec: number | null;
  cached: boolean;
  stale: boolean;
  motion: Motion;
  /**
   * Seam S1 (13e) — the vehicle-state vocabulary. OPTIONAL on the wire like
   * `trackerHealth`/`lastMovingCourse`: a frame from a cold projection buffer may
   * omit it, so `applyFrame` RETAINS the prior state rather than blanking it to
   * `unknown` on a reconnect. Normalised on apply, never trusted verbatim.
   */
  state?: VehicleState;
}

export type LiveFrameType = 'position' | 'status';

export interface LiveFrame {
  type: LiveFrameType;
  /** OUR vehicle uuid — matches an item's `id`. Never a Traccar device id. */
  vehicleId: string;
  payload: LiveFrameFields;
  /** epoch ms, server clock, stamped at emit. */
  ts: number;
}

/**
 * Slice 18h — the "your roster changed" push (seam S1), mirrored from
 * `api/src/modules/live/frames.ts` and NEVER imported across trees. It carries no
 * vehicle id by design: `applyFrame` can never add or remove a vehicle, so
 * membership changes cannot ride a frame — they are answered by a re-fetch. The
 * event is deliberately incapable of carrying fleet data.
 *
 *   { type: 'roster', ts }
 */
export interface RosterEvent {
  type: 'roster';
  /** epoch ms, server clock, stamped at emit. */
  ts: number;
}

/**
 * Structural guard for a value parsed off the wire. Deliberately narrow: a frame
 * that does not carry the exact envelope is dropped, never applied — a malformed
 * push must not corrupt the model the map renders.
 */
export function isLiveFrame(value: unknown): value is LiveFrame {
  if (typeof value !== 'object' || value === null) return false;
  const f = value as Record<string, unknown>;
  if (f.type !== 'position' && f.type !== 'status') return false;
  if (typeof f.vehicleId !== 'string' || f.vehicleId.length === 0) return false;
  if (typeof f.ts !== 'number') return false;
  if (typeof f.payload !== 'object' || f.payload === null) return false;
  return true;
}

/**
 * Slice 18h — is this parsed wire value a roster event? Checked in `applyMessage`
 * BEFORE `isLiveFrame`, so a roster event is routed to a re-fetch and can never
 * reach `applyFrame` (whose guard would drop it anyway — `type` is neither
 * 'position' nor 'status'). Narrow on purpose: exactly `type: 'roster'` + numeric
 * `ts`.
 */
export function isRosterEvent(value: unknown): value is RosterEvent {
  if (typeof value !== 'object' || value === null) return false;
  const f = value as Record<string, unknown>;
  return f.type === 'roster' && typeof f.ts === 'number';
}

/**
 * Apply one frame to the cached live response — PURE, so the reducer that keeps
 * the map's model correct under push is unit-tested without a socket or a map.
 *
 *   - No baseline yet (`old` undefined): return unchanged. The initial
 *     `/api/vehicles/live` fetch owns first paint and every item's identity; a
 *     frame cannot manufacture a vehicle the client has never seen.
 *   - Unknown `vehicleId`: return the SAME object (identity-equal), so React and
 *     TanStack Query see no change and nothing re-renders. A vehicle added since
 *     the last fetch is picked up by the 60 s reconcile / reconnect refetch, not
 *     invented here from an identity-less frame.
 *   - Known vehicle: replace ONLY the volatile live fields, keep identity, and
 *     return NEW array + NEW item references so the change actually propagates
 *     (an in-place mutation would leave the map's `useMemo` deps unchanged and
 *     the marker frozen — the failure family this project keeps hitting).
 */
export function applyFrame(
  old: LiveResponse | undefined,
  frame: LiveFrame,
): LiveResponse | undefined {
  if (!old) return old;
  const idx = old.vehicles.findIndex((v) => v.id === frame.vehicleId);
  if (idx === -1) return old;

  const existing = old.vehicles[idx]!;
  const p = frame.payload;
  const merged: LiveVehicle = {
    // Identity — untouched, from the initial fetch. `vehicleType` is stable
    // identity (the body class), not a volatile live field: frames deliberately
    // omit it (LiveFrameFields), so it is carried through here or it would be lost
    // on the first push and every marker would fall back to the dot.
    id: existing.id,
    plate: existing.plate,
    label: existing.label,
    model: existing.model,
    plan: existing.plan,
    vehicleType: existing.vehicleType,
    // 18g — IDENTITY, so it is carried through like `vehicleType` and never read
    // off the frame. A frame does not carry it (a deviceless vehicle has no Redis
    // key and therefore produces no frames at all), so taking it from `p` would
    // set every pushed vehicle to `undefined` and paint the whole live fleet "No
    // tracker" on the first socket message.
    hasTracker: existing.hasTracker,
    // 13f seam S5 — IDENTITY-like for the frame path: the "silent since" instant
    // comes from the tracker-health sweep and is NOT on a live position frame (a
    // stale tracker produces no frames at all), so it is carried through from the
    // initial fetch and refreshed by the 60 s reconcile. Reading it off `p` would
    // set it undefined on every push and blank the silent-since line.
    trackerSilentSince: existing.trackerSilentSince,
    // Volatile — replaced from the frame.
    status: p.status,
    motion: p.motion,
    // Absent on the wire (cold projection buffer) → RETAIN the prior state, like
    // lastMovingCourse/trackerHealth, so a reconnect cannot blank a parked
    // vehicle's state to `unknown`. Present → authoritative, normalised.
    state: p.state !== undefined ? normalizeVehicleState(p.state) : existing.state,
    lat: p.lat,
    lon: p.lon,
    speedKmh: p.speedKmh ?? 0,
    // Absent ⇒ NOT speeding (see LiveFrameFields.speeding). The one field in this
    // function that coalesces rather than retains, on purpose.
    speeding: p.speeding ?? false,
    // 15e — the sub-line discriminator travels on the frame like every other live
    // field. Absent ⇒ keep what the initial fetch gave us rather than inventing a
    // value: a wrong `lastFixMoving` picks a sub-line that states an observation
    // we never made.
    lastFixMoving: p.lastFixMoving !== undefined ? p.lastFixMoving : existing.lastFixMoving,
    // Course coalesces to NULL, never 0: a null course on a moving frame must fall
    // to the dot via resolveMarker's finite-check, exactly as the fetch path does —
    // 0 would rotate a silhouette due north. (Latent today: a moving derivation
    // carries a real course; this keeps the two ingest paths from disagreeing.)
    course: p.course ?? null,
    // RETAIN the prior heading when a frame omits it or sends null. This is NOT
    // dead: the outgoing WS projection is built from an in-process buffer that is
    // COLD after any Traccar-socket reconnect (deploy/blip), so a parked vehicle's
    // next stopped frame arrives with lastMovingCourse null even though it is known.
    // Taking null verbatim would flip the parked silhouette to a dot until it next
    // drives (hours). A genuinely never-moved vehicle is already null in `existing`,
    // so it stays null; the only value→null case is Redis TTL expiry, by which point
    // the vehicle is stale/offline and resolves to the dot regardless.
    lastMovingCourse: p.lastMovingCourse ?? existing.lastMovingCourse,
    // Absent on the wire (`undefined`) → retain last-known health, like a cold-buffer
    // frame; present (object OR explicit null) → authoritative, normalised. This
    // distinguishes "the frame didn't carry it" from "no telemetry now", so the
    // transition to no-telemetry is not swallowed the way a `?? existing` would.
    trackerHealth:
      p.trackerHealth === undefined
        ? existing.trackerHealth
        : normalizeTrackerHealth(p.trackerHealth),
    positionFixedAt: p.positionFixedAt,
    positionAgeSec: p.positionAgeSec,
    trackerContactAt: p.trackerContactAt,
    freshnessDiverged: p.freshnessDiverged,
    cacheAgeSec: p.cacheAgeSec,
    cached: p.cached,
    stale: p.stale,
  };

  const vehicles = old.vehicles.slice();
  vehicles[idx] = merged;
  return { ...old, vehicles };
}
